import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Send, Bot, User, Loader2, Wifi, WifiOff, AlertCircle, MessageSquare, RefreshCw, Mic, MicOff, Volume2, VolumeX, Music, Disc3 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { ReleaseService } from '../services/releaseService';

type MentionItem =
  | { kind: 'artist'; id: string | number; name: string }
  | { kind: 'release'; id: string; name: string; artistName?: string };

export interface OpenClawMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

export function JanaChat() {
  const [messages, setMessages] = useState<OpenClawMessage[]>([]);
  const [input, setInput] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { user } = useAuth();
  const sessionKeyRef = useRef<string>(`feedbacktool-${Date.now()}`);
  const sessionStartedRef = useRef<boolean>(false);
  const [messageCount, setMessageCount] = useState(0);
  const lastActivityRef = useRef<number>(Date.now());
  const SESSION_MESSAGE_LIMIT = 20; // Neue Session nach 20 Nachrichten
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 Minuten Inaktivität

  // @-Mention Picker State
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState<number>(-1);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionItems, setMentionItems] = useState<MentionItem[]>([]);
  const mentionLoadedRef = useRef(false);

  // OpenClaw Gateway Konfiguration
  const GATEWAY_URL = import.meta.env.VITE_OPENCLAW_GATEWAY_URL || 'http://localhost:18789';
  const GATEWAY_TOKEN = import.meta.env.VITE_OPENCLAW_GATEWAY_TOKEN || '';

  // Konvertiere ws:// zu http:// für API-Calls
  const getApiUrl = () => {
    let url = GATEWAY_URL;
    if (url.startsWith('ws://')) url = url.replace('ws://', 'http://');
    if (url.startsWith('wss://')) url = url.replace('wss://', 'https://');
    return url.replace(/\/$/, '');
  };

  // Generiere neuen Session Key
  const resetSession = useCallback(() => {
    const newSessionKey = `feedbacktool-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    sessionKeyRef.current = newSessionKey;
    setMessageCount(0);
    lastActivityRef.current = Date.now();
    setMessages([]);
    sessionStartedRef.current = false;
    console.log('[JanaChat] Neue Session:', newSessionKey);
  }, []);

  useEffect(() => {
    if (!GATEWAY_TOKEN) {
      setError('OpenClaw Gateway Token nicht konfiguriert. Bitte VITE_OPENCLAW_GATEWAY_TOKEN setzen.');
      return;
    }

    // Prüfe Speech API Support
    const hasSpeech = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
    const hasTTS = 'speechSynthesis' in window;
    setSpeechSupported(hasSpeech && hasTTS);

    setConnectionStatus('connected');
  }, [GATEWAY_TOKEN]);

  // Text-to-Speech für Jana's Antworten
  const speak = useCallback((text: string) => {
    if (!voiceEnabled || !window.speechSynthesis) return;
    
    // Stoppe vorherige Sprache
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'de-DE';
    utterance.rate = 1.1;
    utterance.pitch = 1.0;
    
    // Versuche deutsche Stimme zu finden
    const voices = window.speechSynthesis.getVoices();
    const deVoice = voices.find(v => v.lang.startsWith('de'));
    if (deVoice) utterance.voice = deVoice;
    
    window.speechSynthesis.speak(utterance);
  }, [voiceEnabled]);

  // Speech-to-Text starten
  const startListening = useCallback(() => {
    if (!speechSupported) {
      setError('Spracherkennung wird von diesem Browser nicht unterstützt. Bitte Chrome/Edge/Safari nutzen.');
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'de-DE';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    // Baseline input (Text vor Aufnahmestart) merken, damit wir live anhängen können
    let baseline = '';
    setInput(prev => { baseline = prev; return prev; });

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onresult = (event: any) => {
      let finalText = '';
      let interimText = '';
      for (let i = 0; i < event.results.length; i++) {
        const res = event.results[i];
        const t = res[0].transcript;
        if (res.isFinal) finalText += t;
        else interimText += t;
      }
      const combined = (finalText + interimText).trim();
      setInput(baseline + (baseline && combined ? ' ' : '') + combined);
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        setError('Mikrofon-Zugriff verweigert. Bitte Berechtigungen in den Browsereinstellungen erlauben.');
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [speechSupported]);

  // Speech-to-Text stoppen
  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  // Lies letzte Nachricht vor wenn voiceEnabled sich ändert
  useEffect(() => {
    if (voiceEnabled && messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.role === 'assistant') {
        speak(lastMsg.content);
      }
    }
  }, [voiceEnabled, messages, speak]);

  // Kein Auto-Reset mehr: Chat bleibt bis User manuell "Neuer Chat" klickt

  // Auto-scroll zu neuer Nachricht
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Lazy-load Artists + Releases beim ersten @-Trigger
  const ensureMentionData = useCallback(async () => {
    if (mentionLoadedRef.current) return;
    mentionLoadedRef.current = true;
    try {
      const [{ data: artistsData }, releases] = await Promise.all([
        supabase.from('artists').select('id, name').order('name'),
        ReleaseService.getReleases(),
      ]);
      const artistMap = new Map<string, string>();
      (artistsData || []).forEach((a: any) => artistMap.set(String(a.id), a.name));
      const items: MentionItem[] = [
        ...((artistsData || []) as any[]).map((a) => ({ kind: 'artist' as const, id: a.id, name: a.name })),
        ...releases.map((r) => ({
          kind: 'release' as const,
          id: r.id,
          name: r.title,
          artistName: r.artist_id ? artistMap.get(String(r.artist_id)) : undefined,
        })),
      ];
      setMentionItems(items);
    } catch (e) {
      console.error('Mention data load failed:', e);
    }
  }, []);

  // Gefilterte Mention-Liste
  const filteredMentions = useMemo(() => {
    const q = mentionQuery.toLowerCase().trim();
    const list = q
      ? mentionItems.filter((it) =>
          it.name.toLowerCase().includes(q) ||
          (it.kind === 'release' && it.artistName?.toLowerCase().includes(q))
        )
      : mentionItems;
    return list.slice(0, 8);
  }, [mentionItems, mentionQuery]);

  // Reset Index wenn Filter sich ändert
  useEffect(() => {
    setMentionIndex(0);
  }, [mentionQuery, mentionOpen]);

  // Erkennt aktuelle @-Token an Caret-Position
  const detectMention = (value: string, caret: number) => {
    const before = value.slice(0, caret);
    const match = before.match(/(?:^|\s)@([\w\-\.]*)$/);
    if (match) {
      const start = before.length - match[0].length + (match[0].startsWith('@') ? 0 : 1);
      return { start, query: match[1] };
    }
    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInput(value);
    const caret = e.target.selectionStart ?? value.length;
    const m = detectMention(value, caret);
    if (m) {
      setMentionStart(m.start);
      setMentionQuery(m.query);
      setMentionOpen(true);
      ensureMentionData();
    } else if (mentionOpen) {
      setMentionOpen(false);
      setMentionStart(-1);
      setMentionQuery('');
    }
  };

  const sanitizeName = (name: string) => name.replace(/\s+/g, '_');

  const insertMention = (item: MentionItem) => {
    if (mentionStart < 0) return;
    const before = input.slice(0, mentionStart);
    const afterCaret = textareaRef.current?.selectionStart ?? input.length;
    const after = input.slice(afterCaret);
    const token = `@${item.kind}:${sanitizeName(item.name)} `;
    const next = before + token + after;
    setInput(next);
    setMentionOpen(false);
    setMentionStart(-1);
    setMentionQuery('');
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        const pos = (before + token).length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }
    });
  };

  const sendMessageHTTP = async (history: OpenClawMessage[], content: string): Promise<string> => {
    const apiUrl = getApiUrl();
    // Baue vollständigen Conversation-Context (ohne welcome/system-Hinweise)
    const apiMessages = [
      ...history
        .filter(m => (m.role === 'user' || m.role === 'assistant') && m.id !== 'welcome')
        .map(m => ({ role: m.role, content: m.content })),
      { role: 'user' as const, content },
    ];
    const response = await fetch(`${apiUrl}/v1/chat/completions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${GATEWAY_TOKEN}`,
        'Content-Type': 'application/json',
        'x-openclaw-session-key': sessionKeyRef.current,
      },
      body: JSON.stringify({
        model: 'openclaw/default',
        messages: apiMessages,
        stream: false,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.choices?.[0]?.message?.content || 'Keine Antwort erhalten.';
  };

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    // Session beim ersten Senden lazy initialisieren
    if (!sessionStartedRef.current) {
      const newSessionKey = `feedbacktool-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      sessionKeyRef.current = newSessionKey;
      sessionStartedRef.current = true;
      lastActivityRef.current = Date.now();
      console.log('[JanaChat] Neue Session:', newSessionKey);
    }

    const userMessage: OpenClawMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await sendMessageHTTP(messages, userMessage.content);
      
      const assistantMessage: OpenClawMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Voice Output
      if (voiceEnabled) {
        speak(response);
      }
      
      // Nachrichten zählen (nur für UI), KEIN Auto-Reset mehr
      setMessageCount(prev => prev + 2);
      lastActivityRef.current = Date.now();
    } catch (err: any) {
      console.error('Sendefehler:', err);
      setError(`Fehler: ${err.message}`);
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'system',
        content: `❌ Fehler: ${err.message}`,
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionOpen && filteredMentions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % filteredMentions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + filteredMentions.length) % filteredMentions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMentions[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <Wifi className="h-4 w-4 text-green-400" />;
      case 'connecting':
        return <Loader2 className="h-4 w-4 text-yellow-400 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-400" />;
      default:
        return <WifiOff className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Verbunden';
      case 'connecting':
        return 'Verbinden...';
      case 'error':
        return 'Verbindungsfehler';
      default:
        return 'Getrennt';
    }
  };

  return (
    <div className="flex flex-col h-screen bg-black">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-gray-800">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#0000fe] rounded-lg">
            <Bot className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">Jana</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetSession}
            className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-colors"
            title="Neuer Chat"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-700" title={getStatusText()}>
            {getStatusIcon()}
          </div>
        </div>
      </div>

      {/* Fehlermeldung */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-4 mt-4 p-3 bg-red-900/30 border border-red-700 rounded-lg"
        >
          <p className="text-sm text-red-300">{error}</p>
        </motion.div>
      )}

      {/* Nachrichten-Container */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        <AnimatePresence>
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`flex gap-3 max-w-[80%] ${
                  msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                }`}
              >
                {/* Avatar */}
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    msg.role === 'assistant'
                      ? 'bg-[#0000fe]'
                      : msg.role === 'system'
                      ? 'bg-yellow-600'
                      : 'bg-gray-600'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <Bot className="h-4 w-4 text-white" />
                  ) : msg.role === 'system' ? (
                    <AlertCircle className="h-4 w-4 text-white" />
                  ) : (
                    <User className="h-4 w-4 text-white" />
                  )}
                </div>

                {/* Nachrichten-Bubble */}
                <div
                  className={`px-4 py-2 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-[#efefef] text-gray-900 rounded-br-none'
                      : msg.role === 'system'
                      ? 'bg-yellow-900/30 text-yellow-200 border border-yellow-700/30 rounded-bl-none'
                      : 'bg-gray-700 text-gray-100 rounded-bl-none'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  <p className="text-xs mt-1 opacity-60">
                    {msg.timestamp.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Loading-Indikator */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="flex gap-3 max-w-[80%]">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#0000fe] flex items-center justify-center">
                <Bot className="h-4 w-4 text-white animate-pulse" />
              </div>
              <div className="px-4 py-3 rounded-2xl bg-gray-700 rounded-bl-none">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Eingabebereich */}
      <div className="border-t border-gray-700 bg-[#1A1C1F] px-4 py-4">
        <div className="flex gap-2 max-w-4xl mx-auto">
          <div className="flex-1 relative">
            {/* Mention Picker */}
            {mentionOpen && filteredMentions.length > 0 && (
              <div className="absolute bottom-full mb-2 left-0 right-0 max-w-md bg-gray-800 border border-gray-600 rounded-xl shadow-2xl overflow-hidden z-50">
                <div className="px-3 py-2 text-xs text-gray-400 border-b border-gray-700 bg-gray-900/50">
                  {mentionQuery ? `Suche: "${mentionQuery}"` : 'Artist oder Release einfügen'}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {filteredMentions.map((item, idx) => (
                    <button
                      key={`${item.kind}-${item.id}`}
                      onMouseDown={(e) => { e.preventDefault(); insertMention(item); }}
                      onMouseEnter={() => setMentionIndex(idx)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                        idx === mentionIndex ? 'bg-blue-600/30 text-white' : 'text-gray-200 hover:bg-gray-700/50'
                      }`}
                    >
                      <div className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
                        item.kind === 'artist' ? 'bg-purple-600/30 text-purple-300' : 'bg-emerald-600/30 text-emerald-300'
                      }`}>
                        {item.kind === 'artist' ? <Music className="h-3.5 w-3.5" /> : <Disc3 className="h-3.5 w-3.5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium">{item.name}</div>
                        <div className="text-xs text-gray-400 truncate">
                          {item.kind === 'artist' ? 'Artist' : `Release${item.artistName ? ` · ${item.artistName}` : ''}`}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="px-3 py-1.5 text-[10px] text-gray-500 border-t border-gray-700 bg-gray-900/50">
                  ↑↓ navigieren · ⏎ einfügen · Esc schließen
                </div>
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={isListening ? 'Sprich jetzt...' : 'Schreibe eine Nachricht an Jana... (@ für Artist/Release)'}
              className="w-full px-4 py-3 pr-24 bg-[#374151] border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={1}
              disabled={isLoading}
              style={{ minHeight: '48px', maxHeight: '120px' }}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
              {speechSupported && (
                <button
                  onClick={isListening ? stopListening : startListening}
                  disabled={isLoading}
                  className={`p-2 rounded-lg transition-colors ${
                    isListening
                      ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse'
                      : 'bg-gray-600 hover:bg-gray-500 text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={isListening ? 'Aufnahme stoppen' : 'Spracheingabe'}
                >
                  {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </button>
              )}
              <button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="p-2 bg-[#0000fe] text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JanaChat;